import React, { useState, useEffect, useMemo, useRef } from 'react';
import { X, Edit2, Save, MapPin, Clock, DollarSign, Users, FileText, Phone, Mail, User, Package, TrendingUp, ShoppingBag, Music, Image, Upload, ExternalLink, ChevronLeft, ChevronRight, Download, File, Eye, FolderOpen, Palette, Calendar, Trash2, CalendarRange, Copy } from 'lucide-react';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { formatEventDateRange, getEventDayCount, getMultiDayLabel } from '../../utils/multiDayUtils';
import toast from 'react-hot-toast';
import { useSwipeGesture } from '../../hooks/useSwipeGesture';

// Color options for other events
const EVENT_COLORS = [
  { value: 'orange', label: 'Orange', bg: 'bg-orange-500', light: 'bg-orange-100 dark:bg-orange-900/30' },
  { value: 'purple', label: 'Purple', bg: 'bg-purple-500', light: 'bg-purple-100 dark:bg-purple-900/30' },
  { value: 'green', label: 'Green', bg: 'bg-green-500', light: 'bg-green-100 dark:bg-green-900/30' },
  { value: 'pink', label: 'Pink', bg: 'bg-pink-500', light: 'bg-pink-100 dark:bg-pink-900/30' },
  { value: 'cyan', label: 'Cyan', bg: 'bg-cyan-500', light: 'bg-cyan-100 dark:bg-cyan-900/30' },
  { value: 'yellow', label: 'Yellow', bg: 'bg-yellow-500', light: 'bg-yellow-100 dark:bg-yellow-900/30' },
  { value: 'red', label: 'Red', bg: 'bg-red-500', light: 'bg-red-100 dark:bg-red-900/30' },
  { value: 'indigo', label: 'Indigo', bg: 'bg-indigo-500', light: 'bg-indigo-100 dark:bg-indigo-900/30' },
];

// Helper to format 24h time to 12h
const formatTime12h = (time) => {
  if (!time) return '';
  const [hours, minutes] = time.split(':');
  const h = parseInt(hours, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

// Helper to format date from dateKey or date object
const formatShowDate = (show) => {
  if (show.isMultiDay && show.startDate && show.endDate) {
    return formatEventDateRange(show);
  }
  if (show.dateKey) {
    const [year, month, day] = show.dateKey.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return formatDate(date);
  }
  if (show.date) {
    const date = new Date(show.date);
    if (!isNaN(date.getTime())) {
      return formatDate(date);
    }
  }
  return 'Date not set';
};

// Helper to format a single date for display
const formatSingleDate = (dateString) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', { 
    weekday: 'short',
    month: 'short', 
    day: 'numeric',
    year: 'numeric'
  });
};

// Duplicate Event Modal Component
const DuplicateEventModal = ({ show, isOpen, onClose, onDuplicate }) => {
  const [selectedDate, setSelectedDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [keepMultiDay, setKeepMultiDay] = useState(show?.isMultiDay || false);

  useEffect(() => {
    if (isOpen && show) {
      let dayAfterEvent;
      
      if (show.isMultiDay && show.endDate) {
        const [year, month, day] = show.endDate.split('-').map(Number);
        dayAfterEvent = new Date(year, month - 1, day);
        dayAfterEvent.setDate(dayAfterEvent.getDate() + 1);
      } else if (show.startDate) {
        const [year, month, day] = show.startDate.split('-').map(Number);
        dayAfterEvent = new Date(year, month - 1, day);
        dayAfterEvent.setDate(dayAfterEvent.getDate() + 1);
      } else if (show.dateKey) {
        const [year, month, day] = show.dateKey.split('-').map(Number);
        dayAfterEvent = new Date(year, month - 1, day);
        dayAfterEvent.setDate(dayAfterEvent.getDate() + 1);
      } else {
        dayAfterEvent = new Date();
        dayAfterEvent.setDate(dayAfterEvent.getDate() + 1);
      }
      
      setSelectedDate(dayAfterEvent.toISOString().split('T')[0]);
      
      if (show.isMultiDay && show.startDate && show.endDate) {
        const duration = getEventDayCount(show) - 1;
        const newEnd = new Date(dayAfterEvent);
        newEnd.setDate(newEnd.getDate() + duration);
        setEndDate(newEnd.toISOString().split('T')[0]);
        setKeepMultiDay(true);
      } else {
        setEndDate('');
        setKeepMultiDay(false);
      }
    }
  }, [isOpen, show]);

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
    
    if (keepMultiDay && show?.isMultiDay && show.startDate && show.endDate) {
      const duration = getEventDayCount(show) - 1;
      const newEnd = new Date(newDate);
      newEnd.setDate(newEnd.getDate() + duration);
      setEndDate(newEnd.toISOString().split('T')[0]);
    }
  };

  const handleDuplicate = () => {
    if (!selectedDate) {
      toast.error('Please select a date');
      return;
    }

    const cleanObject = (obj) => {
      const cleaned = {};
      for (const [key, value] of Object.entries(obj)) {
        if (value === undefined) continue;
        if (value === null) {
          cleaned[key] = '';
        } else if (Array.isArray(value)) {
          cleaned[key] = value.filter(item => item !== undefined);
        } else if (typeof value === 'object' && value !== null) {
          cleaned[key] = cleanObject(value);
        } else {
          cleaned[key] = value;
        }
      }
      return cleaned;
    };

    const duplicatedEvent = cleanObject({
      title: show.title || '',
      venue: show.venue || '',
      city: show.city || '',
      loadInTime: show.loadInTime || '',
      showTime: show.showTime || '',
      guarantee: show.guarantee || '',
      contactName: show.contactName || '',
      contactEmail: show.contactEmail || '',
      contactPhone: show.contactPhone || '',
      contactTags: show.contactTags || [],
      eventType: show.eventType || 'show',
      personalCategory: show.personalCategory || 'travel',
      startTime: show.startTime || '',
      endTime: show.endTime || '',
      eventColor: show.eventColor || 'orange',
      gas: show.gas || '',
      hotel: show.hotel || '',
      food: show.food || '',
      tolls: show.tolls || '',
      otherExpenses: show.otherExpenses || '',
      showNotes: show.showNotes || '',
      setlist: show.setlist || '',
      crowdInfo: show.crowdInfo || '',
      technicalNotes: show.technicalNotes || '',
      nextTime: show.nextTime || '',
      bands: show.bands || [],
      images: show.images || [],
      status: show.eventType === 'show' ? 'tentative' : (show.status || 'confirmed'),
      isMultiDay: keepMultiDay && show.isMultiDay ? true : false,
      startDate: selectedDate,
      endDate: keepMultiDay && show.isMultiDay ? (endDate || '') : '',
    });

    onDuplicate(selectedDate, duplicatedEvent);
    onClose();
    toast.success('Event duplicated!');
  };

  if (!isOpen) return null;

  const dayCount = keepMultiDay && selectedDate && endDate 
    ? getEventDayCount({ isMultiDay: true, startDate: selectedDate, endDate }) 
    : 1;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-50 animate-fade-in">
      <div className="modal-enter bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-sm w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
            <Copy size={20} className="text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100">Duplicate Event</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-[200px]">"{show?.title}"</p>
          </div>
        </div>

        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {keepMultiDay ? 'Start Date' : 'New Date'}
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => handleDateChange(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {show?.isMultiDay && (
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
              <div className="flex items-center gap-2">
                <CalendarRange size={16} className="text-purple-500" />
                <span className="text-sm text-gray-700 dark:text-gray-300">Keep as multi-day event</span>
              </div>
              <button
                type="button"
                onClick={() => setKeepMultiDay(!keepMultiDay)}
                className={`btn-press relative w-10 h-5 rounded-full transition-colors ${keepMultiDay ? 'bg-purple-500' : 'bg-gray-300 dark:bg-gray-600'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${keepMultiDay ? 'translate-x-5' : 'translate-x-0'}`} />
              </button>
            </div>
          )}

          {keepMultiDay && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                min={selectedDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              {dayCount > 1 && (
                <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                  {dayCount} days
                </p>
              )}
            </div>
          )}

          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
            <p className="text-xs text-amber-700 dark:text-amber-300">
              <strong>Note:</strong> Financial data (merch sales) will not be copied. {show?.eventType === 'show' && 'Status will be reset to "Tentative".'}
            </p>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="btn-press flex-1 px-4 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleDuplicate}
            disabled={!selectedDate}
            className="btn-press flex-1 px-4 py-2.5 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <Copy size={16} />
            Duplicate
          </button>
        </div>
      </div>
    </div>
  );
};

// ShowMerchSales Component
const ShowMerchSales = ({ show, formatCurrencyFn }) => {
  const merchSales = show?.merchSales || [];
  const cashMerch = parseFloat(show?.cashMerch || 0);
  
  const totals = useMemo(() => {
    let itemsSold = 0;
    const itemBreakdown = {};

    merchSales.forEach(sale => {
      const qty = sale.quantity || 1;
      itemsSold += qty;
      
      const key = `${sale.itemName}-${sale.size || 'N/A'}`;
      if (!itemBreakdown[key]) {
        itemBreakdown[key] = { 
          name: sale.itemName, 
          category: sale.category, 
          size: sale.size, 
          quantity: 0, 
          revenue: 0, 
          price: sale.price 
        };
      }
      itemBreakdown[key].quantity += qty;
      itemBreakdown[key].revenue += sale.total || (qty * sale.price) || 0;
    });

    return { itemsSold, itemBreakdown: Object.values(itemBreakdown) };
  }, [merchSales]);

  const formatMoney = (amount) => formatCurrencyFn ? formatCurrencyFn(amount) : `$${parseFloat(amount || 0).toFixed(2)}`;

  if (merchSales.length === 0 && cashMerch === 0) {
    return (
      <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Package size={20} className="text-purple-600 dark:text-purple-400" />
          <h3 className="font-semibold text-gray-800 dark:text-gray-100">Merch Sales</h3>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No sales recorded for this show yet.</p>
        <p className="text-xs text-gray-400 dark:text-gray-500 text-center">Use Sales Mode during the show to track merch sales automatically.</p>
      </div>
    );
  }

  if (merchSales.length === 0 && cashMerch > 0) {
    return (
      <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-4">
          <ShoppingBag size={20} className="text-purple-600 dark:text-purple-400" />
          <h3 className="font-semibold text-gray-800 dark:text-gray-100">Merch Sales</h3>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Total Revenue</p>
          <p className="text-3xl font-bold text-green-600 dark:text-green-400">{formatMoney(cashMerch)}</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">Itemized breakdown not available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-4">
        <ShoppingBag size={20} className="text-purple-600 dark:text-purple-400" />
        <h3 className="font-semibold text-gray-800 dark:text-gray-100">Merch Sales</h3>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">Items Sold</p>
          <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{totals.itemsSold}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">Revenue</p>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400">{formatMoney(cashMerch)}</p>
        </div>
      </div>
      {totals.itemBreakdown.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Breakdown</p>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {totals.itemBreakdown.sort((a, b) => b.quantity - a.quantity).map((item, idx) => (
              <div key={idx} className="flex items-center justify-between py-2 px-3 bg-white dark:bg-gray-800 rounded-lg text-sm">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-800 dark:text-gray-100 truncate">{item.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{item.size && item.size !== 'N/A' && `${item.size} • `}{item.quantity}x @ {formatMoney(item.price)}</p>
                </div>
                <p className="font-bold text-green-600 dark:text-green-400 ml-2">{formatMoney(item.revenue)}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Image Gallery Component
const ShowImageGallery = ({ images = [], isEditing, onImagesChange, onNavigateToDocuments, onAddDocument = null, showId = null, showTitle = '' }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const newImages = [];
    for (const file of files) {
      const isImage = file.type.startsWith('image/');
      const isPDF = file.type === 'application/pdf';
      const isWord = file.type === 'application/msword' || file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      
      if (!isImage && !isPDF && !isWord) continue;
      if (images.length + newImages.length >= 5) break;

      try {
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        
        const imageId = Date.now() + Math.random();
        const imageData = {
          id: imageId,
          name: file.name,
          type: file.type,
          size: file.size,
          data: dataUrl,
          uploadedAt: new Date().toISOString()
        };
        
        newImages.push(imageData);
        
        if (onAddDocument) {
          const docEntry = {
            id: `show-doc-${imageId}`,
            name: file.name,
            originalName: file.name,
            size: file.size,
            type: file.type,
            data: dataUrl,
            uploadedAt: new Date().toISOString(),
            category: isImage ? 'promo' : 'other',
            showId: showId || null,
            tags: showTitle ? [showTitle] : [],
            notes: showTitle ? `Uploaded from show: ${showTitle}` : 'Uploaded from show details',
            favorite: false,
            fromShow: true
          };
          onAddDocument(docEntry);
        }
      } catch (err) {
        console.error('Error reading file:', err);
      }
    }

    onImagesChange([...images, ...newImages]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (imageId) => onImagesChange(images.filter(img => img.id !== imageId));
  
  const openLightbox = (index) => { 
    const img = images[index];
    if (img.type?.startsWith('image/')) {
      setCurrentIndex(index); 
      setSelectedImage(img); 
    } else {
      window.open(img.data, '_blank');
    }
  };
  
  const closeLightbox = () => setSelectedImage(null);
  const goToPrevious = () => { const i = currentIndex === 0 ? images.length - 1 : currentIndex - 1; setCurrentIndex(i); setSelectedImage(images[i]); };
  const goToNext = () => { const i = currentIndex === images.length - 1 ? 0 : currentIndex + 1; setCurrentIndex(i); setSelectedImage(images[i]); };

  const getFileIcon = (type) => {
    if (type === 'application/pdf') return '📄';
    if (type === 'application/msword' || type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return '📝';
    return '📎';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (images.length === 0 && !isEditing) return null;

  return (
    <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Image size={20} className="text-indigo-600 dark:text-indigo-400" />
          <h3 className="font-semibold text-gray-800 dark:text-gray-100">Flyers & Documents</h3>
          {images.length > 0 && (
            <span className="text-xs px-2 py-0.5 bg-indigo-200 dark:bg-indigo-800 text-indigo-700 dark:text-indigo-300 rounded-full">{images.length}</span>
          )}
        </div>
        {images.length > 0 && onNavigateToDocuments && (
          <button onClick={onNavigateToDocuments} className="btn-press text-xs text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1">
            View in Documents <ExternalLink size={12} />
          </button>
        )}
      </div>

      {images.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-3">
          {images.map((img, idx) => {
            const isImageFile = img.type?.startsWith('image/');
            
            return (
              <div key={img.id} className="relative group">
                <div 
                  className="card-tap aspect-[4/3] rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 cursor-pointer flex items-center justify-center" 
                  onClick={() => openLightbox(idx)}
                >
                  {isImageFile ? (
                    <img src={img.data} alt={img.name} className="w-full h-full object-cover hover:scale-105 transition-transform" />
                  ) : (
                    <div className="flex flex-col items-center justify-center p-2 text-center hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors w-full h-full">
                      <span className="text-2xl mb-1">{getFileIcon(img.type)}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 truncate w-full px-1">{img.name}</span>
                      <span className="text-xs text-gray-400 dark:text-gray-500">{formatFileSize(img.size)}</span>
                    </div>
                  )}
                </div>
                {isEditing && (
                  <button onClick={(e) => { e.stopPropagation(); removeImage(img.id); }} className="btn-press absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
                    <X size={12} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {isEditing && images.length < 5 && (
        <div>
          <input 
            ref={fileInputRef} 
            type="file" 
            accept="image/*,application/pdf,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" 
            multiple 
            onChange={handleFileSelect} 
            className="hidden" 
          />
          <button onClick={() => fileInputRef.current?.click()} className="btn-press w-full flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-indigo-300 dark:border-indigo-600 rounded-lg text-indigo-600 dark:text-indigo-400 hover:border-indigo-400 dark:hover:border-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors text-sm">
            <Upload size={16} /> Add Document
          </button>
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">{images.length}/5 files • PNG, JPG, PDF, DOCX</p>
        </div>
      )}

      {images.length === 0 && !isEditing && (
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">No flyers or documents attached</p>
      )}

      {selectedImage && selectedImage.type?.startsWith('image/') && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 animate-fade-in" onClick={closeLightbox}>
          <button onClick={(e) => { e.stopPropagation(); closeLightbox(); }} className="btn-press absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors">
            <X size={24} />
          </button>
          {images.filter(img => img.type?.startsWith('image/')).length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); goToPrevious(); }} className="btn-press absolute left-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"><ChevronLeft size={24} /></button>
              <button onClick={(e) => { e.stopPropagation(); goToNext(); }} className="btn-press absolute right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"><ChevronRight size={24} /></button>
            </>
          )}
          <img src={selectedImage.data} alt={selectedImage.name} className="max-w-full max-h-[90vh] object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-4 py-2 rounded-full">{currentIndex + 1} / {images.length} • {selectedImage.name}</div>
        </div>
      )}
    </div>
  );
};

// Show Documents Component
const ShowDocuments = ({ documents = [], showId, onNavigateToDocuments }) => {
  const [previewDoc, setPreviewDoc] = useState(null);
  const docsArray = Array.isArray(documents) ? documents : [];
  
  const showDocuments = useMemo(() => {
    if (!showId) return [];
    const showIdStr = String(showId);
    return docsArray.filter(doc => doc.showId && String(doc.showId) === showIdStr);
  }, [docsArray, showId]);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (type) => {
    if (type?.startsWith('image/')) return Image;
    if (type === 'application/pdf') return FileText;
    return File;
  };

  const canPreview = (type) => type?.startsWith('image/') || type === 'application/pdf';

  const getDocumentUrl = (doc) => {
    if (doc.downloadUrl) return doc.downloadUrl;
    if (doc.data) return doc.data;
    return null;
  };

  const getCategoryColor = (category) => {
    const colors = {
      contracts: 'bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400',
      riders: 'bg-purple-100 text-purple-600 dark:bg-purple-900/40 dark:text-purple-400',
      stagePlots: 'bg-green-100 text-green-600 dark:bg-green-900/40 dark:text-green-400',
      receipts: 'bg-orange-100 text-orange-600 dark:bg-orange-900/40 dark:text-orange-400',
      promo: 'bg-pink-100 text-pink-600 dark:bg-pink-900/40 dark:text-pink-400',
      other: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
    };
    return colors[category] || colors.other;
  };

  const getCategoryName = (category) => {
    const names = { contracts: 'Contract', riders: 'Rider', stagePlots: 'Stage Plot', receipts: 'Receipt', promo: 'Promo', other: 'Other' };
    return names[category] || 'Document';
  };

  if (showDocuments.length === 0) {
    if (onNavigateToDocuments) {
      return (
        <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FolderOpen size={20} className="text-gray-400" />
              <h3 className="font-semibold text-gray-600 dark:text-gray-400">Documents</h3>
            </div>
            <button onClick={onNavigateToDocuments} className="btn-press text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1">
              Link documents <ExternalLink size={12} />
            </button>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">No documents linked to this show yet.</p>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FolderOpen size={20} className="text-blue-600 dark:text-blue-400" />
          <h3 className="font-semibold text-gray-800 dark:text-gray-100">Documents</h3>
          <span className="text-xs px-2 py-0.5 bg-blue-200 dark:bg-blue-800 text-blue-700 dark:text-blue-300 rounded-full">{showDocuments.length}</span>
        </div>
        {onNavigateToDocuments && (
          <button onClick={onNavigateToDocuments} className="btn-press text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1">
            Manage in Documents <ExternalLink size={12} />
          </button>
        )}
      </div>

      <div className="space-y-2">
        {showDocuments.map(doc => {
          const Icon = getFileIcon(doc.type);
          const docUrl = getDocumentUrl(doc);
          return (
            <div key={doc.id} className="card-tap flex items-center gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${getCategoryColor(doc.category)}`}>
                {doc.type?.startsWith('image/') && docUrl ? (
                  <img src={docUrl} alt="" className="w-full h-full object-cover rounded-lg" />
                ) : (
                  <Icon size={20} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-800 dark:text-gray-100 truncate text-sm">{doc.name}</p>
                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <span className={`px-1.5 py-0.5 rounded ${getCategoryColor(doc.category)}`}>{getCategoryName(doc.category)}</span>
                  <span>{formatFileSize(doc.size)}</span>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {canPreview(doc.type) && docUrl && (
                  <button onClick={() => setPreviewDoc(doc)} className="btn-press p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600 rounded-lg transition-colors" title="Preview">
                    <Eye size={16} />
                  </button>
                )}
                {docUrl && (
                  <a href={docUrl} download={doc.name} target="_blank" rel="noopener noreferrer" className="btn-press p-2 hover:bg-green-100 dark:hover:bg-green-900/30 text-green-600 rounded-lg transition-colors" title="Download">
                    <Download size={16} />
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {previewDoc && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 animate-fade-in" onClick={() => setPreviewDoc(null)}>
          <div className="modal-enter bg-white dark:bg-gray-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-800 dark:text-gray-100">{previewDoc.name}</h3>
                <p className="text-sm text-gray-500">{formatFileSize(previewDoc.size)} • {new Date(previewDoc.uploadedAt).toLocaleDateString()}</p>
              </div>
              <div className="flex items-center gap-2">
                {getDocumentUrl(previewDoc) && (
                  <a href={getDocumentUrl(previewDoc)} download={previewDoc.name} target="_blank" rel="noopener noreferrer" className="btn-press flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm">
                    <Download size={16} />Download
                  </a>
                )}
                <button onClick={() => setPreviewDoc(null)} className="btn-press p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="overflow-auto max-h-[calc(90vh-80px)]">
              {previewDoc.type?.startsWith('image/') && getDocumentUrl(previewDoc) ? (
                <img src={getDocumentUrl(previewDoc)} alt={previewDoc.name} className="w-full h-auto" />
              ) : previewDoc.type === 'application/pdf' && getDocumentUrl(previewDoc) ? (
                <iframe src={getDocumentUrl(previewDoc)} className="w-full h-[80vh] border-0" title={previewDoc.name} />
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Multi-Day Date Range Editor Component
const MultiDayEditor = ({ formData, setFormData, show }) => {
  const handleToggle = () => {
    if (!formData.isMultiDay) {
      setFormData({
        ...formData,
        isMultiDay: true,
        startDate: formData.startDate || show.dateKey || show.startDate,
        endDate: formData.endDate || show.dateKey || show.startDate
      });
    } else {
      setFormData({ ...formData, isMultiDay: false, startDate: null, endDate: null });
    }
  };

  const dayCount = formData.isMultiDay && formData.startDate && formData.endDate
    ? getEventDayCount({ isMultiDay: true, startDate: formData.startDate, endDate: formData.endDate })
    : 1;

  return (
    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CalendarRange size={18} className="text-purple-600 dark:text-purple-400" />
          <h3 className="font-semibold text-gray-800 dark:text-gray-100">Multi-Day Event</h3>
        </div>
        <button
          type="button"
          onClick={handleToggle}
          className={`btn-press relative w-12 h-6 rounded-full transition-colors ${formData.isMultiDay ? 'bg-purple-500' : 'bg-gray-300 dark:bg-gray-600'}`}
        >
          <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${formData.isMultiDay ? 'translate-x-6' : 'translate-x-0'}`} />
        </button>
      </div>
      
      {formData.isMultiDay && (
        <div className="space-y-3">
          <p className="text-xs text-gray-500 dark:text-gray-400">This event spans multiple days (festival, tour, etc.)</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Start Date</label>
              <input
                type="date"
                value={formData.startDate || ''}
                onChange={(e) => {
                  const newStartDate = e.target.value;
                  setFormData({ 
                    ...formData, 
                    startDate: newStartDate,
                    endDate: formData.endDate && newStartDate > formData.endDate ? newStartDate : formData.endDate
                  });
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-purple-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">End Date</label>
              <input
                type="date"
                value={formData.endDate || ''}
                min={formData.startDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-purple-500 text-sm"
              />
            </div>
          </div>
          {formData.startDate && formData.endDate && (
            <div className="flex items-center gap-2 text-sm">
              <span className="px-2 py-1 bg-purple-200 dark:bg-purple-800 text-purple-700 dark:text-purple-300 rounded-lg font-medium">
                {dayCount} day{dayCount > 1 ? 's' : ''}
              </span>
              <span className="text-gray-500 dark:text-gray-400">
                {formatSingleDate(formData.startDate)} → {formatSingleDate(formData.endDate)}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Multi-Day Display Component (for view mode)
const MultiDayDisplay = ({ show }) => {
  if (!show.isMultiDay || !show.startDate || !show.endDate) return null;
  const dayCount = getEventDayCount(show);

  return (
    <div className="flex items-center gap-2 mt-2 flex-wrap">
      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-lg">
        <CalendarRange size={16} />
        <span className="font-medium text-sm">{dayCount} day event</span>
      </div>
      <span className="text-sm text-gray-500 dark:text-gray-400">
        {formatSingleDate(show.startDate)} → {formatSingleDate(show.endDate)}
      </span>
    </div>
  );
};

const ShowDetailModal = ({ show, isOpen, onClose, onUpdate, onDelete, onAdd, onNavigate, documents = [], onAddDocument = null }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const contentRef = useRef(null);

  const isOtherEvent = show?.eventType === 'other' || show?.eventType === 'personal';

  const handleDelete = () => {
    if (show.dateKey && show.id && onDelete) {
      const storageKey = show.isMultiDay && show.startDate ? show.startDate : show.dateKey;
      onDelete(storageKey, show.id);
      onClose();
    }
  };

  const handleDuplicate = (dateKey, eventData) => {
    if (onAdd) {
      onAdd(dateKey, eventData);
    }
  };

  useEffect(() => {
    if (show) {
      setFormData({
        title: show.title || '', venue: show.venue || '', city: show.city || '',
        loadInTime: show.loadInTime || '', showTime: show.showTime || '',
        startTime: show.startTime || '', endTime: show.endTime || '',
        eventColor: show.eventColor || 'orange', eventType: show.eventType || 'show',
        isMultiDay: show.isMultiDay || false, startDate: show.startDate || null, endDate: show.endDate || null,
        guarantee: show.guarantee || '', cashMerch: show.cashMerch || '', venmoMerch: show.venmoMerch || '',
        contactName: show.contactName || '', contactEmail: show.contactEmail || '', contactPhone: show.contactPhone || '',
        gas: show.gas || '', hotel: show.hotel || '', food: show.food || '', tolls: show.tolls || '', otherExpenses: show.otherExpenses || '',
        showNotes: show.showNotes || '', setlist: show.setlist || '', crowdInfo: show.crowdInfo || '',
        technicalNotes: show.technicalNotes || '', nextTime: show.nextTime || '',
        bands: show.bands || [], images: show.images || []
      });
    }
  }, [show]);

  // iOS Safari fix: Force input focus on touchstart
  useEffect(() => {
    if (!isOpen) return;
    
    const handleTouchStart = (e) => {
      const target = e.target;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
        setTimeout(() => {
          target.focus();
        }, 10);
      }
    };
    
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
    };
  }, [isOpen]);

  if (!isOpen || !show) return null;

  const handleSave = () => {
    if (!show.id) {
      toast.error('Error: Cannot update event - missing information');
      return;
    }
    const originalStorageKey = show.isMultiDay && show.startDate ? show.startDate : show.dateKey;
    onUpdate(originalStorageKey, show.id, formData);
    setIsEditing(false);
    toast.success('Event updated successfully!');
  };

  const handleCancel = () => {
    setFormData({
      title: show.title || '', venue: show.venue || '', city: show.city || '',
      loadInTime: show.loadInTime || '', showTime: show.showTime || '',
      startTime: show.startTime || '', endTime: show.endTime || '',
      eventColor: show.eventColor || 'orange', eventType: show.eventType || 'show',
      isMultiDay: show.isMultiDay || false, startDate: show.startDate || null, endDate: show.endDate || null,
      guarantee: show.guarantee || '', cashMerch: show.cashMerch || '', venmoMerch: show.venmoMerch || '',
      contactName: show.contactName || '', contactEmail: show.contactEmail || '', contactPhone: show.contactPhone || '',
      gas: show.gas || '', hotel: show.hotel || '', food: show.food || '', tolls: show.tolls || '', otherExpenses: show.otherExpenses || '',
      showNotes: show.showNotes || '', setlist: show.setlist || '', crowdInfo: show.crowdInfo || '',
      technicalNotes: show.technicalNotes || '', nextTime: show.nextTime || '',
      bands: show.bands || [], images: show.images || []
    });
    setIsEditing(false);
  };

  const handleImagesChange = (newImages) => setFormData(prev => ({ ...prev, images: newImages }));

  const totalIncome = parseFloat(formData.guarantee || 0) + parseFloat(formData.cashMerch || 0) + parseFloat(formData.venmoMerch || 0);
  const totalExpenses = parseFloat(formData.gas || 0) + parseFloat(formData.hotel || 0) + parseFloat(formData.food || 0) + parseFloat(formData.tolls || 0) + parseFloat(formData.otherExpenses || 0);
  const netProfit = totalIncome - totalExpenses;

  const eventColorConfig = EVENT_COLORS.find(c => c.value === (formData.eventColor || show.eventColor)) || EVENT_COLORS[0];

  // Render for "Other" event types (simplified view)
  if (isOtherEvent) {
    return (
      <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center animate-fade-in">
        <div className="absolute inset-0 bg-black opacity-50" onClick={onClose} />
        <div className="modal-enter relative bg-white dark:bg-gray-800 rounded-t-2xl md:rounded-xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col">
          {/* Drag Handle - Mobile Only */}
          <div className="md:hidden flex justify-center pt-3 pb-1 cursor-grab active:cursor-grabbing">
            <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full" />
          </div>

          {/* Header */}
          <div className={`flex items-center justify-between p-4 sm:p-6 border-b-4 ${eventColorConfig.bg.replace('bg-', 'border-')}`}>
            <div className="flex-1 min-w-0">
              {isEditing ? (
                <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100 bg-transparent border-b-2 border-blue-500 focus:outline-none w-full" placeholder="Event Title" />
              ) : (
                <h2 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100 truncate">{show.title || 'Untitled Event'}</h2>
              )}
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 flex items-center gap-2">
                <Calendar size={14} />
                {formatShowDate(show)}
              </p>
              {!isEditing && <MultiDayDisplay show={show} />}
            </div>
            <div className="flex items-center gap-1 ml-2 shrink-0">
              {!isEditing ? (
                <>
                  <button onClick={() => setShowDuplicateModal(true)} className="btn-press p-2 text-purple-600 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded-lg transition-colors" title="Duplicate Event"><Copy size={18} /></button>
                  <button onClick={() => setIsEditing(true)} className="btn-press p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-colors" title="Edit Event"><Edit2 size={18} /></button>
                  <button onClick={() => setShowDeleteConfirm(true)} className="btn-press p-2 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors" title="Delete Event"><Trash2 size={18} /></button>
                </>
              ) : (
                <>
                  <button onClick={handleSave} className="btn-press flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"><Save size={16} /><span className="hidden sm:inline">Save</span></button>
                  <button onClick={handleCancel} className="btn-press px-3 sm:px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors text-sm">Cancel</button>
                </>
              )}
              <button onClick={onClose} className="btn-press p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"><X size={20} /></button>
            </div>
          </div>

          {/* Scrollable Content */}
          <div 
            ref={contentRef} 
            className="flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6 space-y-4" 
            style={{ touchAction: 'auto', WebkitOverflowScrolling: 'touch' }}
          >
            {isEditing && <MultiDayEditor formData={formData} setFormData={setFormData} show={show} />}

            <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <Clock size={16} className="text-blue-500" />Time
              </h3>
              {isEditing ? (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Start Time</label>
                    <input type="time" value={formData.startTime} onChange={(e) => setFormData({ ...formData, startTime: e.target.value })} className="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">End Time</label>
                    <input type="time" value={formData.endTime} onChange={(e) => setFormData({ ...formData, endTime: e.target.value })} className="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500 text-sm" />
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                  <Clock size={16} />
                  {show.startTime ? <span>{formatTime12h(show.startTime)}{show.endTime && ` - ${formatTime12h(show.endTime)}`}</span> : <span className="text-gray-400">No time set</span>}
                </div>
              )}
            </div>

            {isEditing && (
              <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <Palette size={16} className="text-purple-500" />Calendar Color
                </h3>
                <div className="flex flex-wrap gap-2">
                  {EVENT_COLORS.map(color => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, eventColor: color.value })}
                      className={`btn-press w-8 h-8 rounded-full ${color.bg} transition-all ${formData.eventColor === color.value ? 'ring-2 ring-offset-2 ring-blue-500 dark:ring-offset-gray-800 scale-110' : 'hover:scale-105'}`}
                      title={color.label}
                    />
                  ))}
                </div>
              </div>
            )}

            {(show.showNotes || isEditing) && (
              <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <FileText size={16} className="text-orange-500" />Notes
                </h3>
                {isEditing ? (
                  <textarea value={formData.showNotes} onChange={(e) => setFormData({ ...formData, showNotes: e.target.value })} placeholder="Add notes..." rows="3" className="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500 text-sm resize-none" />
                ) : (
                  <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{show.showNotes}</p>
                )}
              </div>
            )}

            {!isEditing && (
              <div className="space-y-2">
                <button onClick={() => setShowDuplicateModal(true)} className="btn-press w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/40 transition-colors font-medium">
                  <Copy size={18} />Duplicate Event
                </button>
                <button onClick={() => setShowDeleteConfirm(true)} className="btn-press w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors font-medium">
                  <Trash2 size={18} />Delete Event
                </button>
              </div>
            )}
          </div>

          {showDeleteConfirm && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-50 animate-fade-in">
              <div className="modal-enter bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-sm w-full p-6">
                <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-2">Delete Event?</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Are you sure you want to delete "{show.title}"? This action cannot be undone.</p>
                <div className="flex gap-3">
                  <button onClick={() => setShowDeleteConfirm(false)} className="btn-press flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">Cancel</button>
                  <button onClick={handleDelete} className="btn-press flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors">Delete</button>
                </div>
              </div>
            </div>
          )}

          <DuplicateEventModal
            show={show}
            isOpen={showDuplicateModal}
            onClose={() => setShowDuplicateModal(false)}
            onDuplicate={handleDuplicate}
          />
        </div>
      </div>
    );
  }

  // Render for Show/Gig events (full view with financials)
  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center animate-fade-in">
      <div className="absolute inset-0 bg-black opacity-50" onClick={onClose} />
      <div className="modal-enter relative bg-white dark:bg-gray-800 rounded-t-2xl md:rounded-xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        {/* Drag Handle - Mobile Only */}
        <div className="md:hidden flex justify-center pt-3 pb-1 cursor-grab active:cursor-grabbing">
          <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex-1 min-w-0">
            {isEditing ? (
              <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100 bg-transparent border-b-2 border-blue-500 focus:outline-none w-full" placeholder="Show Title" />
            ) : (
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100 truncate">{show.title || 'Untitled Show'}</h2>
            )}
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{formatShowDate(show)}</p>
            {!isEditing && <MultiDayDisplay show={show} />}
          </div>
          <div className="flex items-center gap-1 ml-2 shrink-0">
            {!isEditing ? (
              <>
                <button onClick={() => setShowDuplicateModal(true)} className="btn-press p-2 text-purple-600 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded-lg transition-colors" title="Duplicate Show"><Copy size={18} /></button>
                <button onClick={() => setIsEditing(true)} className="btn-press p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-colors" title="Edit Show"><Edit2 size={18} /></button>
                <button onClick={() => setShowDeleteConfirm(true)} className="btn-press p-2 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors" title="Delete Show"><Trash2 size={18} /></button>
              </>
            ) : (
              <>
                <button onClick={handleSave} className="btn-press flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"><Save size={16} /><span className="hidden sm:inline">Save</span></button>
                <button onClick={handleCancel} className="btn-press px-3 sm:px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors text-sm">Cancel</button>
              </>
            )}
            <button onClick={onClose} className="btn-press p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"><X size={20} /></button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div 
          ref={contentRef} 
          className="flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6" 
          style={{ touchAction: 'auto', WebkitOverflowScrolling: 'touch' }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2"><MapPin size={20} className="text-purple-600" />Show Details</h3>
              
              {isEditing && <MultiDayEditor formData={formData} setFormData={setFormData} show={show} />}
              
              {isEditing ? (
                <>
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Venue</label>
                    <input type="text" value={formData.venue} onChange={(e) => setFormData({ ...formData, venue: e.target.value })} placeholder="Venue name" className="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">City</label>
                    <input type="text" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} placeholder="City" className="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500 text-sm" />
                  </div>
                </>
              ) : (
                (show.venue || show.city) && (
                  <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                    <MapPin size={16} />
                    <span>{show.venue || 'No venue'}{show.city && `, ${show.city}`}</span>
                  </div>
                )
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Load In</label>
                  {isEditing ? (
                    <input type="time" value={formData.loadInTime} onChange={(e) => setFormData({ ...formData, loadInTime: e.target.value })} className="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500 text-sm" />
                  ) : (
                    <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300"><Clock size={16} /><span>{show.loadInTime ? formatTime12h(show.loadInTime) : 'N/A'}</span></div>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Show Time</label>
                  {isEditing ? (
                    <input type="time" value={formData.showTime} onChange={(e) => setFormData({ ...formData, showTime: e.target.value })} className="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500 text-sm" />
                  ) : (
                    <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300"><Clock size={16} /><span>{show.showTime ? formatTime12h(show.showTime) : 'N/A'}</span></div>
                  )}
                </div>
              </div>

              {((show.bands && show.bands.length > 0) || isEditing) && (
                <div>
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-2 flex items-center gap-1"><Music size={14} />Bands / Artists</label>
                  {isEditing ? (
                    <div className="space-y-2">
                      {(formData.bands || []).map((band, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <input type="text" value={band} onChange={(e) => { const newBands = [...(formData.bands || [])]; newBands[index] = e.target.value; setFormData({ ...formData, bands: newBands }); }} className="flex-1 px-3 py-2 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500 text-sm" />
                          <button type="button" onClick={() => { const newBands = (formData.bands || []).filter((_, i) => i !== index); setFormData({ ...formData, bands: newBands }); }} className="btn-press p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"><X size={16} /></button>
                        </div>
                      ))}
                      <button type="button" onClick={() => { const newBands = [...(formData.bands || []), '']; setFormData({ ...formData, bands: newBands }); }} className="btn-press w-full px-3 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 rounded-lg hover:border-blue-500 hover:text-blue-500 transition-colors text-sm">+ Add Band</button>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {(show.bands || []).map((band, index) => (
                        <span key={index} className="px-3 py-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-sm font-medium">{band}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2"><DollarSign size={20} className="text-green-600" />Financials</h3>

              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Guarantee</label>
                  {isEditing ? (
                    <input type="number" value={formData.guarantee} onChange={(e) => setFormData({ ...formData, guarantee: e.target.value })} placeholder="0" className="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500 text-sm" />
                  ) : (
                    <p className="text-lg font-semibold text-green-600 dark:text-green-400">{formatCurrency(parseFloat(show.guarantee || 0))}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Cash Merch</label>
                    {isEditing ? (
                      <input type="number" value={formData.cashMerch} onChange={(e) => setFormData({ ...formData, cashMerch: e.target.value })} placeholder="0" className="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500 text-sm" />
                    ) : (
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">{formatCurrency(parseFloat(show.cashMerch || 0))}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Venmo Merch</label>
                    {isEditing ? (
                      <input type="number" value={formData.venmoMerch} onChange={(e) => setFormData({ ...formData, venmoMerch: e.target.value })} placeholder="0" className="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500 text-sm" />
                    ) : (
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">{formatCurrency(parseFloat(show.venmoMerch || 0))}</p>
                    )}
                  </div>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                  <p className="text-xs text-gray-600 dark:text-gray-400">Total Income</p>
                  <p className="text-xl font-bold text-blue-600 dark:text-blue-400">{formatCurrency(totalIncome)}</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2"><FileText size={20} className="text-orange-600" />Expenses</h3>

              <div className="grid grid-cols-2 gap-3">
                {['gas', 'hotel', 'food', 'tolls', 'otherExpenses'].map(field => (
                  <div key={field}>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1 capitalize">{field === 'otherExpenses' ? 'Other' : field}</label>
                    {isEditing ? (
                      <input type="number" value={formData[field]} onChange={(e) => setFormData({ ...formData, [field]: e.target.value })} placeholder="0" className="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500 text-sm" />
                    ) : (
                      <p className="text-sm font-semibold text-red-600 dark:text-red-400">{formatCurrency(parseFloat(show[field] || 0))}</p>
                    )}
                  </div>
                ))}
              </div>

              <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                <p className="text-xs text-gray-600 dark:text-gray-400">Total Expenses</p>
                <p className="text-xl font-bold text-red-600 dark:text-red-400">{formatCurrency(totalExpenses)}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${netProfit >= 0 ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
                <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-2">Net Profit</h3>
                <p className={`text-3xl font-bold ${netProfit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{formatCurrency(netProfit)}</p>
              </div>

              <ShowImageGallery
                images={isEditing ? formData.images : (show.images || [])}
                isEditing={isEditing}
                onImagesChange={handleImagesChange}
                onNavigateToDocuments={onNavigate ? () => onNavigate('documents') : null}
                onAddDocument={onAddDocument}
                showId={show.id}
                showTitle={show.title || ''}
              />

              <ShowDocuments
                documents={documents}
                showId={show.id}
                onNavigateToDocuments={onNavigate ? () => onNavigate('documents') : null}
              />

              <ShowMerchSales show={show} formatCurrencyFn={formatCurrency} />
            </div>

            {(show.contactName || show.contactEmail || show.contactPhone || isEditing) && (
              <div className="space-y-4 md:col-span-2">
                <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2"><User size={20} className="text-indigo-600" />Contact Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Name</label>
                    {isEditing ? (
                      <input type="text" value={formData.contactName} onChange={(e) => setFormData({ ...formData, contactName: e.target.value })} placeholder="Contact name" className="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500 text-sm" />
                    ) : (
                      <p className="text-sm text-gray-700 dark:text-gray-300">{show.contactName || 'N/A'}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Email</label>
                    {isEditing ? (
                      <input type="email" value={formData.contactEmail} onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })} placeholder="email@example.com" className="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500 text-sm" />
                    ) : (
                      <p className="text-sm text-gray-700 dark:text-gray-300">{show.contactEmail || 'N/A'}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Phone</label>
                    {isEditing ? (
                      <input type="tel" value={formData.contactPhone} onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })} placeholder="(555) 123-4567" className="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500 text-sm" />
                    ) : (
                      <p className="text-sm text-gray-700 dark:text-gray-300">{show.contactPhone || 'N/A'}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {(show.showNotes || show.setlist || show.crowdInfo || show.technicalNotes || show.nextTime || isEditing) && (
              <div className="space-y-4 md:col-span-2">
                <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100">Notes</h3>
                {isEditing ? (
                  <div className="space-y-3">
                    <textarea value={formData.showNotes} onChange={(e) => setFormData({ ...formData, showNotes: e.target.value })} placeholder="General notes..." rows="2" className="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500 text-sm" />
                    <textarea value={formData.setlist} onChange={(e) => setFormData({ ...formData, setlist: e.target.value })} placeholder="Setlist..." rows="2" className="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500 text-sm" />
                    <input type="text" value={formData.crowdInfo} onChange={(e) => setFormData({ ...formData, crowdInfo: e.target.value })} placeholder="Crowd info..." className="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500 text-sm" />
                    <textarea value={formData.technicalNotes} onChange={(e) => setFormData({ ...formData, technicalNotes: e.target.value })} placeholder="Technical notes..." rows="2" className="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500 text-sm" />
                    <input type="text" value={formData.nextTime} onChange={(e) => setFormData({ ...formData, nextTime: e.target.value })} placeholder="For next time..." className="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500 text-sm" />
                  </div>
                ) : (
                  <div className="space-y-3 text-sm">
                    {show.showNotes && <div><p className="font-semibold text-gray-700 dark:text-gray-300">General:</p><p className="text-gray-600 dark:text-gray-400">{show.showNotes}</p></div>}
                    {show.setlist && <div><p className="font-semibold text-gray-700 dark:text-gray-300">Setlist:</p><p className="text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{show.setlist}</p></div>}
                    {show.crowdInfo && <div><p className="font-semibold text-gray-700 dark:text-gray-300">Crowd:</p><p className="text-gray-600 dark:text-gray-400">{show.crowdInfo}</p></div>}
                    {show.technicalNotes && <div><p className="font-semibold text-gray-700 dark:text-gray-300">Technical:</p><p className="text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{show.technicalNotes}</p></div>}
                    {show.nextTime && <div><p className="font-semibold text-gray-700 dark:text-gray-300">Next Time:</p><p className="text-gray-600 dark:text-gray-400">{show.nextTime}</p></div>}
                  </div>
                )}
              </div>
            )}

            {!isEditing && (
              <div className="md:col-span-2 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-2">
                <button onClick={() => setShowDuplicateModal(true)} className="btn-press w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/40 transition-colors font-medium">
                  <Copy size={18} />Duplicate Show
                </button>
                <button onClick={() => setShowDeleteConfirm(true)} className="btn-press w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors font-medium">
                  <Trash2 size={18} />Delete Show
                </button>
              </div>
            )}
          </div>
        </div>

        {showDeleteConfirm && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-50 animate-fade-in">
            <div className="modal-enter bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-sm w-full p-6">
              <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-2">Delete Show?</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Are you sure you want to delete "{show.title}"? This action cannot be undone.</p>
              <div className="flex gap-3">
                <button onClick={() => setShowDeleteConfirm(false)} className="btn-press flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">Cancel</button>
                <button onClick={handleDelete} className="btn-press flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors">Delete</button>
              </div>
            </div>
          </div>
        )}

        <DuplicateEventModal
          show={show}
          isOpen={showDuplicateModal}
          onClose={() => setShowDuplicateModal(false)}
          onDuplicate={handleDuplicate}
        />
      </div>
    </div>
  );
};

export default ShowDetailModal;